import { nanoid } from "nanoid";
import React from "react";

import { Button } from "~/components/button";
import { DragAndDrop } from "~/components/drag-and-drop";
import { Draggable } from "~/components/drag-and-drop/draggable";
import { DraggablePlaceholder } from "~/components/drag-and-drop/draggable-placeholder";
import { Droppable } from "~/components/drag-and-drop/droppable";
import { DragHandle } from "~/components/drag-and-drop/handle";
import { FormField } from "~/components/form/field";
import { FormLabel } from "~/components/form/label";
import { FormFieldReset } from "~/components/form/reset";
import { IconDragHandle } from "~/components/icon";
import { flat } from "~/tools/flat";
import { groupBy } from "~/tools/group-by";
import { useScopedEffect } from "~/tools/impulse";
import { map } from "~/tools/map";
import { pickBy } from "~/tools/pick-by";
import { pipe } from "~/tools/pipe";
import { prop } from "~/tools/prop";
import { reorder } from "~/tools/reorder";
import { toValues } from "~/tools/to-values";

import { type FormState, initStaticValueState } from "./_form-state";
import { StaticValue } from "./_static-value";

function useValidateDuplicates(state: FormState): void {
	useScopedEffect(
		(scope) => {
			// reset all errors
			state.setError(null);

			const values = state.getElements(scope);

			const duplicates = pipe(
				values,
				map(({ fields }) => ({
					/**
					 * What is visible at the end is the label with a fallback to value,
					 * so we assign the error to the label field as it is responsible for the final display.
					 */
					field: fields.label,
					label: fields.label.getInput(scope).trim() || fields.value.getInput(scope).trim(),
				})),
				groupBy(prop("label")),
				pickBy((list) => list.length > 1),
				toValues(),
				flat(),
			);

			for (const { field } of duplicates) {
				field.setError(["Duplicated value label"]);
			}
		},
		[state],
	);
}

export interface VariableFormProps {
	canReorder: boolean;
	state: FormState;
}

export const VariableForm: React.FC<VariableFormProps> = ({ canReorder, state }) => {
	useValidateDuplicates(state);

	return (
		<FormField className="items-stretch" state={state}>
			<FormLabel asChild>
				<div>
					<FormLabel>Values</FormLabel>
					<FormFieldReset />
				</div>
			</FormLabel>

			{(scope, field) => (
				<DragAndDrop
					onDragEnd={({ source, destination }) => {
						if (destination) {
							state.setElements((values) => reorder(values, source.index, destination.index));
						}
					}}
				>
					<Droppable className="-mt-2">
						{field.getElements(scope).map((value, index, arr) => {
							const id = value.fields.id(scope);
							const isDragEnabled = canReorder && arr.length > 1;

							return (
								<Draggable asChild index={index} isDragDisabled={!isDragEnabled} key={id}>
									<StaticValue
										className="mt-2"
										dragHandle={
											isDragEnabled && (
												<DragHandle asChild>
													<Button asChild icon size="sm" variant="neutral">
														<span>
															<IconDragHandle className="text-gray-500" />
														</span>
													</Button>
												</DragHandle>
											)
										}
										onDelete={
											arr.length > 1
												? () => {
														state.setElements((values) => values.filter(({ fields }) => fields.id(scope) !== id));
													}
												: undefined
										}
										state={value}
									/>
								</Draggable>
							);
						})}

						<DraggablePlaceholder />
					</Droppable>
				</DragAndDrop>
			)}

			<Button
				className="mt-4 uppercase"
				onClick={() => {
					state.setElements((values) => [...values, initStaticValueState(nanoid())]);
				}}
				variant="primary"
			>
				Add value
			</Button>
		</FormField>
	);
};
